import React from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  TooltipProps,
} from "recharts";

import { AlertEvent } from "../../hooks/useThermalAlerts";
import { formatTime } from "../../hooks/ThermalHeatMapHooks";

// ─── Shared constants (duplicated here to avoid circular imports) ─────────────

export const FACE_COLORS: Record<string, string> = {
  PX:   "#36F097",
  NX:   "#5BC0D7",
  PY:   "#CCF54E",
  NY:   "#EFA514",
  PZ:   "#F43FD0",
  NZ:   "#4B8BFF",
  INT1: "#A45ED4",
  INT2: "#FC0C6D",
};

// Helper to get color for a key (including _max/_min suffixes)
export function getFaceColor(key: string): string {
  const baseKey = key.replace(/_(max|min)$/, '');
  const color = FACE_COLORS[baseKey] || '#888';
  if (key.endsWith('_min')) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, 0.6)`;
  }
  return color;
}

// ─── Props ────────────────────────────────────────────────────────────────────



export interface ChartContentProps {
  chartData: Record<string, number>[];
  visibleFaceKeys: string[];
  thresholdLines: { key: string; y: number; color: string }[];
  breachAreas: { x1: number; x2: number; color: string; stroke: string }[];
  renderTooltip: (props: TooltipProps<number, string>) => React.ReactNode;
  onMouseEnterFace: (key: string) => void;
  onMouseLeaveFace: () => void;
  alertMarkers?: AlertEvent[];
}

// ─── Component ────────────────────────────────────────────────────────────────

const ThermalHeatMapChartContent: React.FC<ChartContentProps> = ({
  chartData,
  visibleFaceKeys,
  thresholdLines,
  breachAreas,
  renderTooltip,
  onMouseEnterFace,
  onMouseLeaveFace,
  alertMarkers = [],
}) => (
  <ResponsiveContainer width="100%" height="100%">
    <ComposedChart
      data={chartData}
      margin={{ top: 8, right: 20, bottom: 24, left: 8 }}
    >
      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.18)" />
      <XAxis
        dataKey="time"
        type="number"
        domain={["dataMin", "dataMax"]}
        tickFormatter={(v: number) => formatTime(v)}
        tick={{ fill: "#909090", fontSize: 10 }}
        stroke="#444"
        label={{
          value: "Time",
          position: "insideBottom",
          offset: -12,
          fill: "rgba(144,144,144,0.7)",
          fontSize: 10,
        }}
      />
      <YAxis
        tick={{ fill: "#909090", fontSize: 10 }}
        stroke="#444"
        tickFormatter={(v: number) => `${v}°`}
        width={40}
        label={{
          value: "Temperature (°C)",
          angle: -90,
          position: "insideLeft",
          offset: 12,
          fill: "rgba(144,144,144,0.7)",
          fontSize: 10,
          dx: -8,
          style: { textAnchor: "middle" },
        }}
      />
      <Tooltip content={renderTooltip} />

      {/* Vertical breach bands — rendered first so they sit behind lines */}
      {breachAreas.map((area, i) => (
        <ReferenceArea
          key={i}
          x1={area.x1}
          x2={area.x2}
          fill={area.color}
          stroke={area.stroke}
          strokeOpacity={0.75}
          strokeWidth={0.75}
          ifOverflow="hidden"
        />
      ))}

      {/* Horizontal threshold reference lines */}
      {thresholdLines.map((tl) => (
        <ReferenceLine
          key={tl.key}
          y={tl.y}
          stroke={tl.color}
          strokeDasharray="5 3"
          strokeWidth={1}
        />
      ))}

      {/* Alert markers as vertical lines */}
      {alertMarkers.map((alert, i) => (
        <ReferenceLine
          key={`alert-marker-${i}`}
          x={alert.timeSec}
          stroke="#ff3333"
          strokeDasharray="2 2"
          strokeWidth={2}
          label={false}
        />
      ))}

      {/* One line per visible face/deck */}
      {visibleFaceKeys.map((key) => (
        <Line
          key={key}
          type="monotone"
          dataKey={key}
          stroke={getFaceColor(key)}
          strokeWidth={1.5}
          dot={false}
          activeDot={{ r: 3, strokeWidth: 0 }}
          isAnimationActive={false}
          onMouseEnter={() => onMouseEnterFace(key)}
          onMouseLeave={() => onMouseLeaveFace()}
        />
      ))}
    </ComposedChart>
  </ResponsiveContainer>
);


export default ThermalHeatMapChartContent;
